// ============================================================================
// gamificationService — type re-exports + snapshot hydrator
// ----------------------------------------------------------------------------
// Live transactional writes (spin/claim/streak/cosmetic/track) moved into the
// server-authoritative ExperienceEngine (functions/src/experienceEngine.ts);
// this module now only owns the client-side hydrator used by the live profile
// snapshot listener, plus the shared type aliases.
// ============================================================================

import type { ActivityTrackId, SpinTier, UserGamificationProfile } from '../types/schema'
import { createDefaultGamificationProfile } from '../utils/gamificationRegistry'
export {
  applyXp as applyGamificationXp,
  computeLoginUpdate as computeGamificationLoginUpdate,
} from '../utils/gamificationRegistry'

const hydrateProfile = (
  uid: string,
  data: Partial<UserGamificationProfile> | undefined,
  nowIso: string,
): UserGamificationProfile => {
  const base = createDefaultGamificationProfile(uid, nowIso)
  if (!data) return base
  return {
    ...base,
    ...data,
    uid,
    trackProgress: data.trackProgress ?? {},
    unclaimedTrackTickets: data.unclaimedTrackTickets ?? {},
    pinnedBadgeIds: data.pinnedBadgeIds ?? [],
    pinnedTimelineElementIds: data.pinnedTimelineElementIds ?? [],
    equippedAvatarId: data.equippedAvatarId || base.equippedAvatarId,
    equippedTitle: data.equippedTitle || base.equippedTitle,
    equippedCanvasBgId: data.equippedCanvasBgId || base.equippedCanvasBgId,
    equippedThemeId: data.equippedThemeId || base.equippedThemeId,
    equippedAudioId: data.equippedAudioId || base.equippedAudioId,
    equippedOrbitSkinId: data.equippedOrbitSkinId || base.equippedOrbitSkinId,
    unlockedAvatarIds: data.unlockedAvatarIds ?? [],
    unlockedThemeIds: data.unlockedThemeIds ?? [],
    unlockedBackgroundIds: data.unlockedBackgroundIds ?? [],
    unlockedOrbitSkinIds: data.unlockedOrbitSkinIds ?? base.unlockedOrbitSkinIds ?? ['orbit_cream'],
    unlockedTitlePrefixes: data.unlockedTitlePrefixes ?? [],
    unlockedTitleSuffixes: data.unlockedTitleSuffixes ?? [],
  }
}

/** Public hydrator for snapshot listeners (GamificationProvider). */
export const hydrateGamificationProfile = (
  uid: string,
  data: Partial<UserGamificationProfile> | undefined,
  nowIso = new Date().toISOString(),
): UserGamificationProfile => hydrateProfile(uid, data, nowIso)

export interface ClaimTrackTicketsResult {
  trackId: ActivityTrackId
  /** Tickets that moved from the locked bucket into the live counters. */
  collected: Partial<Record<SpinTier, number>>
}

export type EquippableSlotField =
  | 'equippedAvatarId'
  | 'equippedTitle'
  | 'equippedCanvasBgId'
  | 'equippedThemeId'
  | 'equippedAudioId'
  | 'equippedOrbitSkinId'

export type UnlockArrayField =
  | 'unlockedAvatarIds'
  | 'unlockedThemeIds'
  | 'unlockedBackgroundIds'
  | 'unlockedOrbitSkinIds'
  | 'unlockedTitlePrefixes'
  | 'unlockedTitleSuffixes'

export interface ProfileShowcasePatch {
  bioMarkdown?: string
  pinnedBadgeIds?: string[]
  pinnedTimelineElementIds?: string[]
}
